/**
 * Global search contract (`GET /search?q=&limit=`).
 *
 * One request answers the header palette: results come back grouped by kind, already filtered by what the
 * caller may read. A kind the caller cannot read is left out, never answered with a 403.
 *
 * Finance (ADR-0024): when the branch's figures are not disclosed to the caller, the transaction and pledge
 * groups are left out entirely, the same way their own lists refuse them, because a row there names who gave
 * or promised. The amount fields stay nullable so a client never assumes a figure is present.
 *
 * Money is a canonical decimal string (`MoneyString`, IBA-116), never a JSON number, like every other finance
 * payload. Format it with the currency beside it; do not parse it into a float.
 */
import type { MoneyString } from "../finance/money";

export enum SearchResultKind {
  Member = "member",
  Transaction = "transaction",
  Pledge = "pledge",
  Activity = "activity",
  Group = "group",
  Ministry = "ministry",
  Department = "department",
  Form = "form",
  Asset = "asset",
}

/** Order the palette lists the groups in. */
export const SEARCH_RESULT_KIND_ORDER: readonly SearchResultKind[] = [
  SearchResultKind.Member,
  SearchResultKind.Transaction,
  SearchResultKind.Pledge,
  SearchResultKind.Activity,
  SearchResultKind.Group,
  SearchResultKind.Ministry,
  SearchResultKind.Department,
  SearchResultKind.Form,
  SearchResultKind.Asset,
];

export const SEARCH_QUERY_MIN_LENGTH = 2;
export const SEARCH_QUERY_MAX_LENGTH = 100;
export const SEARCH_DEFAULT_LIMIT_PER_KIND = 5;
export const SEARCH_MAX_LIMIT_PER_KIND = 8;

interface SearchResultBase {
  id: string;
  title: string;
  subtitle: string | null;
}

export interface MemberSearchResult extends SearchResultBase {
  kind: SearchResultKind.Member;
  phone: string | null;
  /** Wire value of the member status (lowercase, e.g. "active"). */
  status: string;
  avatarUrl: string | null;
  branchName: string | null;
  groupNames: string[];
  ministryNames: string[];
  occupation: string | null;
}

export interface TransactionSearchResult extends SearchResultBase {
  kind: SearchResultKind.Transaction;
  /** Nullable by contract; see the finance note at the top of this file. */
  amount: MoneyString | null;
  currency: string | null;
  /** ISO date. */
  date: string;
  /** Wire value of the transaction type (e.g. INCOME, EXPENSE). */
  transactionKind: string;
  accountName: string | null;
  reference: string | null;
}

export interface PledgeSearchResult extends SearchResultBase {
  kind: SearchResultKind.Pledge;
  pledgerName: string | null;
  /** `null` for an in-kind pledge, whose figures count units rather than money (and see the finance note above). */
  totalAmount: MoneyString | null;
  fulfilledAmount: MoneyString | null;
  currency: string | null;
  fundName: string | null;
  status: string;
}

export interface ActivitySearchResult extends SearchResultBase {
  kind: SearchResultKind.Activity;
  /** ISO dates. */
  startDate: string | null;
  endDate: string | null;
  status: string;
}

export interface OrganizationUnitSearchResult extends SearchResultBase {
  kind: SearchResultKind.Group | SearchResultKind.Ministry | SearchResultKind.Department;
  description: string | null;
  memberCount: number | null;
  leaderName: string | null;
}

export interface FormSearchResult extends SearchResultBase {
  kind: SearchResultKind.Form;
  status: string;
  responseCount: number | null;
}

export interface AssetSearchResult extends SearchResultBase {
  kind: SearchResultKind.Asset;
  itemCode: string | null;
  status: string;
}

export type SearchResult =
  | MemberSearchResult
  | TransactionSearchResult
  | PledgeSearchResult
  | ActivitySearchResult
  | OrganizationUnitSearchResult
  | FormSearchResult
  | AssetSearchResult;

export interface SearchResultGroup {
  kind: SearchResultKind;
  /** Matches found for this kind (may exceed `items.length`). */
  total: number;
  items: SearchResult[];
}

/** `data` of the `{ data, meta }` envelope (ADR-0003). Groups follow SEARCH_RESULT_KIND_ORDER; empty kinds are omitted. */
export interface SearchResponse {
  groups: SearchResultGroup[];
}
